import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { FilterRequestForCombo, RetailersService, UnregisteredUserRequest, UnregisteredUsersService } from '@src/app/shared/backend/clients';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-unregistered-user',
    templateUrl: './edit-unregistered-user.component.html',
    styleUrl: './edit-unregistered-user.component.scss',
    host: {
        class: 'app-edit-unregistered-user'
    },
    standalone: false
})
export class EditUnregisteredUserComponent extends BaseViewComponent implements OnInit {

  unregisteredUserId: number | null = null;
  unregisteredUser: any = {};

  retailersList: any[] = [];
  retailersTotalRecords: number = 0;
  retailersRowsPerPageOption: number = 5;
  selectedRetailers: number[] = [];

  public unregisteredUserForm: FormGroup = this.fb.group({
    firstname: ['', [Validators.required, Validators.maxLength(100)]],
    lastname: ['', [Validators.required, Validators.maxLength(100)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator()]],
    romanisedLastName: ['', [latinCharsetRegexValidator()]],
    retailerIds: [[], [Validators.required]]
  });

  constructor(
    private changeDetector: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private unregisteredUsersService: UnregisteredUsersService,
    private retailersService: RetailersService,
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.unregisteredUserId = Number(params['unregisteredUserId']);

      if (this.unregisteredUserId) {
        await this.loadAllFormDataAndInit();
        this.changeDetector.detectChanges();
      }
    });
  }

  async loadAllFormDataAndInit(): Promise<void> {
    const details = await firstValueFrom(
      this.unregisteredUsersService.apiUnregisteredUsersDetailsIdGet(this.unregisteredUserId as number)
    );

    const retailerIdsResponse = await firstValueFrom(
      this.unregisteredUsersService.apiUnregisteredUsersIdRetailerIdsGet(this.unregisteredUserId as number)
    );

    this.unregisteredUser = details;

    const assignedRetailerIds: number[] = Array.isArray(retailerIdsResponse?.retailerIds)
      ? retailerIdsResponse.retailerIds.filter((id: any) => id != null)
      : [];

    this.selectedRetailers = assignedRetailerIds;

    const assignedRetailersPage = assignedRetailerIds.length
      ? await firstValueFrom(this.retailersService.apiRetailersFilterComboPost({
        pageNumber: 1,
        pageSize: assignedRetailerIds.length,
        additionalFilters: [
          {
            propertyName: 'id',
            operator: 'in',
            values: assignedRetailerIds.map((id) => String(id))
          }
        ]
      }))
      : { results: [], totalCount: 0 };

    const retailersPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost({
      pageNumber: 1,
      pageSize: this.retailersRowsPerPageOption
    }));

    this.retailersList = this.removeDuplicatesById([
      ...(assignedRetailersPage.results ?? []),
      ...(retailersPage.results ?? [])
    ]);

    this.retailersTotalRecords = retailersPage.totalCount ?? 0;

    this.initForm();
  }

  initForm() {
    this.unregisteredUserForm.patchValue({
      firstname: this.unregisteredUser.firstName ?? null,
      lastname: this.unregisteredUser.lastName ?? null,
      romanisedFirstName: this.unregisteredUser.romanisedFirstName ?? null,
      romanisedLastName: this.unregisteredUser.romanisedLastName ?? null,
      retailerIds: this.selectedRetailers
    });
  }

  onLazyLoadRetailers(event: any) {
    this.getRetailers(event);
  }

  async getRetailers(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.retailersRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const retailersPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(payload));

    this.retailersList = this.removeDuplicatesById([
      ...(this.retailersList ?? []),
      ...(retailersPage.results ?? [])
    ]);
    this.retailersTotalRecords = retailersPage.totalCount ?? 0;
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  private removeDuplicatesById(items: any[]): any[] {
    const uniqueItems: any[] = [];
    const seenIds = new Set();

    for (const item of items) {
      if (!seenIds.has(item?.id)) {
        uniqueItems.push(item);
        seenIds.add(item?.id);
      }
    }

    return uniqueItems;
  }

  isSubmitDisabled(): boolean {
    return this.unregisteredUserForm.invalid;
  }

  async submitForm() {
    if (this.isSubmitDisabled() || !this.unregisteredUserId) {
      return;
    }

    const retailerIds: number[] = (this.unregisteredUserForm.controls['retailerIds'].value || [])
      .map((id: any) => Number(id))
      .filter((id: number) => !Number.isNaN(id));

    const payload: UnregisteredUserRequest = {
      firstName: this.unregisteredUserForm.controls['firstname'].value,
      lastName: this.unregisteredUserForm.controls['lastname'].value,
      romanisedFirstName: this.unregisteredUserForm.controls['romanisedFirstName'].value,
      romanisedLastName: this.unregisteredUserForm.controls['romanisedLastName'].value,
      retailerIds
    };

    await firstValueFrom(
      this.unregisteredUsersService.apiUnregisteredUsersUpdateIdPut(this.unregisteredUserId, payload)
    );

    this.router.navigate(['/unregistered-users', this.unregisteredUserId]);
  }
}
